import axios from "axios";
import router from "@/router";
import {
    hydrateSelectedTenantFromBrowser,
    pickTenantFromContext,
    resolveTenantIdFromBrowser,
} from "../utils/tenant";

const state = {
    tenantData: [],
    selectedTenant: hydrateSelectedTenantFromBrowser(),
    tenantError: null,
    user: null,
    permissions: [],
};

const mutations = {
    SET_TENANT_DATA(state, data) {
        state.tenantData = data;
    },
    SET_SELECTED_TENANT(state, { name, ID }) {
        state.selectedTenant = { name, ID };
        if (name) {
            try {
                localStorage.setItem("tenantName", name);
            } catch {
                void 0;
            }
        }
    },
    SET_TENANT_ERROR(state, error) {
        state.tenantError = error;
    },
    CLEAR_TENANT_ERROR(state) {
        state.tenantError = null;
    },
    SET_USER(state, user) {
        state.user = user;
    },
        SET_PERMISSIONS(state, permissions) {
            state.permissions = permissions || []; 
        },
    
    LOGOUT(state) {
        state.user = null;
        state.permissions = [];
        state.selectedTenant = null;
        const darkModePref = localStorage.getItem("darkMode");
        try {
            localStorage.clear();
        } catch (e) {
            void 0;
        }
        if (darkModePref !== null) {
            localStorage.setItem("darkMode", darkModePref);
        }
        if (typeof caches !== "undefined") {
            caches.delete("metaDataCache").catch(() => {});
        }
    },
};

const actions = {
    async getUserData({ commit, dispatch, getters }) {
        const authToken = localStorage.getItem("authToken");
        if (!authToken) {
            return;
        }
        const tenantId =
            resolveTenantIdFromBrowser() ||
            getters.getSelectedTenant?.ID;
        if (!tenantId) {
            return;
        }
        try {
            const response = await axios.get(
                `${process.env.VUE_APP_API_URL}public/api/admin/user-info`,
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "X-TenantID": tenantId,
                    },
                }
            );

            if (response.data?.statusCode === 200 || response.data?.statusCode === 201) {
                const userData = response.data.data;
                const permissionNames = userData.permissions?.map((perm) => perm.name) || [];
                commit("SET_USER", userData);
                commit("SET_PERMISSIONS", permissionNames);
            } else if (response.data?.statusCode === 401) {
                dispatch("logoutUser");
            } else {
                console.error("Unexpected API response:", response.data);
            }
        } catch (error) {
            console.error("Fetch user error:", error);
            if (error?.response?.status === 401) {
                dispatch("logoutUser");
            }
        }
    },

    syncSelectedTenant({ commit, state }) {
        const tenants = state.tenantData;
        if (!tenants?.length) return;

        const current = state.selectedTenant;
        const matched = pickTenantFromContext(tenants, {
            preferredId: current?.ID,
            preferredName: current?.name || undefined,
        });
        if (!matched) return;

        if (current?.ID === matched.ID && current?.name === matched.name) {
            return;
        }

        commit("SET_SELECTED_TENANT", { name: matched.name, ID: matched.ID });
        try {
            localStorage.setItem("tenantName", matched.name);
        } catch {
            void 0;
        }
    },

    async fetchTenants({ commit, dispatch }) {
        commit("CLEAR_TENANT_ERROR");
        try {
            const response = await axios.get(
                `${process.env.VUE_APP_API_URL}public/api/admin/tenants`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                    timeout: 15000,
                }
            );

            if (response.data.statusCode === 200) {
                commit("SET_TENANT_DATA", response.data.data.tenants);
                dispatch("syncSelectedTenant");
            }
        } catch (error) {
            console.error("Tenant fetch error:", error);
            if (error.code === "ECONNABORTED") {
                commit("SET_TENANT_ERROR", "Request timeout. Try again.");
            }
        }
    },

    logoutUser({ commit }) {
        commit("LOGOUT");
        commit("eda/clear", null, { root: true });
        router.push("/login");
    },
};

const getters = {
    getTenantData: (state) => state.tenantData,
    getSelectedTenant: (state) => state.selectedTenant,
    getTenantError: (state) => state.tenantError,
    getUser: (state) => state.user,
    getPermissions: (state) => state.permissions,

    hasPermission: (state) => (requiredPermission) => {
        return state.permissions.includes(requiredPermission);
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
